import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import BenchlingEntityLink from '@/components/UI/BenchlingEntityLink/BenchlingEntityLink';
import Button from '@/components/UI/Button';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import SelectDropDown from '@/components/UI/SelectDropDown';
import ActionList from '@/components/UI/SelectDropDown/Menus/ActionList';
import Table from '@/components/UI/Table';
import { type TanStackColumnDef } from '@/components/UI/Table/TableComponent.model.ts';
import UserAvatar from '@/components/UI/UserAvatar';
import { successToast } from '@/helpers';
import { getSampleStatusDisplay, type Sample } from '@/model/Sample.model.ts';
import { useFetchEntity } from '@/support/Hooks/fetch';
import { useRequest } from '@/support/Hooks/request';
import { modalAction } from '@/utils/modal';
import { DateUtils } from '@/utils/Date.utils';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';

const plannedSamplesEnabledSelector = createSelector(
  [featuresSelector],
  (features): boolean => features?.planned_samples ?? false
);

const Samples = ({ hasWriteAccess = false }) => {
  const plannedSamplesEnabled = useSelector(plannedSamplesEnabledSelector);
  const { subjectId } = useParams();
  const {
    entity: samples,
    entityLoading: samplesLoading,
    fetchEntity: fetchSamples,
    entityError: fetchError,
  } = useFetchEntity<Array<Sample>>({
    entityType: 'animalSamples',
    params: { id: subjectId },
  });
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);

  const ActionsCell = ({ sample }: { sample: Sample }) => {
    const { sendRequest: deleteSample } = useRequest({
      route: 'samples.destroy',
      method: 'delete',
      params: { id: sample.id },
    });

    return (
      <SelectDropDown
        title="More options"
        iconOnly
        className="hover-near-black"
        alignMenu="right"
        portalProps={{
          width: 120,
        }}
      >
        <ActionList
          actions={[
            {
              action: () =>
                openModal('EDIT_SAMPLE', {
                  sample,
                  handleAfterClose: fetchSamples,
                }),
              name: 'Edit',
            },
            {
              action: () =>
                openModal('CONFIRM_DELETE_SAMPLE', {
                  onClick: async (reasonForDelete?: string) => {
                    await deleteSample({ reason_for_change: reasonForDelete });
                    await fetchSamples();
                    successToast('Deleted.');
                  },
                }),
              className: 'red',
              name: 'Delete',
            },
          ]}
        />
      </SelectDropDown>
    );
  };

  const columns: Array<TanStackColumnDef<Sample>> = [
    {
      id: 'sample_id',
      header: 'ID',
      accessorKey: 'sample_id',
      enableSorting: true,
      cell: ({ row: { original: sample } }: { row: { original: Sample } }) => (
        <div className="flex items-center">
          <BenchlingEntityLink
            entityType="samples"
            apiId={sample.api_id}
            benchlingEntityWebUrl={sample.benchling_entity_web_url}
            visible={sample.synced_to_benchling === true}
          />
          <a
            className="link blue lh-title db"
            onClick={() =>
              openModal('VIEW_SAMPLE', {
                sample,
              })
            }
          >
            {sample.sample_id}
          </a>
        </div>
      ),
    },
    ...(plannedSamplesEnabled
      ? [
          {
            id: 'status',
            header: 'Status',
            accessorKey: 'status',
            enableSorting: true,
            cell: ({ row: { original: sample } }: { row: { original: Sample } }) =>
              getSampleStatusDisplay(sample?.status),
          } as TanStackColumnDef<Sample>,
        ]
      : []),
    {
      id: 'type',
      header: 'Type',
      accessorKey: 'type',
      enableSorting: true,
    },
    ...(plannedSamplesEnabled
      ? [
          {
            id: 'timepoint',
            header: 'Timepoint',
            accessorKey: 'timepoint',
            enableSorting: true,
            cell: ({ row: { original: sample } }: { row: { original: Sample } }) =>
              DateUtils.renderDuration(sample?.timepoint, { format: 'short', zeros: true, defaultResponse: '-' }),
          } as TanStackColumnDef<Sample>,
        ]
      : []),
    {
      id: 'date_taken',
      header: 'Recorded',
      accessorKey: 'date',
      enableSorting: true,
      cell: ({
        row: {
          original: { date },
        },
      }: {
        row: { original: Sample };
      }) => <DateTimeRenderer value={date} defaultResponse="-" />,
    },
    {
      id: 'recorded_by',
      header: 'User',
      accessorFn: (row) => row.created_by?.email,
      size: 100,
      enableSorting: true,
      cell: ({
        row: {
          original: { created_by: createdBy },
        },
      }: {
        row: { original: Sample };
      }) => <UserAvatar user={createdBy} tooltip={createdBy?.name ?? createdBy?.email} />,
    },
    {
      id: 'actions',
      enableSorting: false,
      isVisible: hasWriteAccess,
      size: 75,
      meta: {
        className: 'ui__table--overflow-cell',
      },
      cell: ({ row: { original: sample } }: { row: { original: Sample } }) => <ActionsCell sample={sample} />,
    },
  ];

  if (samplesLoading) {
    return <Loading />;
  }

  return (
    <div className="ui-card" data-test-component="Samples" data-test-element="container">
      <div className="pa3 bb b--moon-gray flex justify-between items-center">
        <h3 className="f5 lh-title">Samples</h3>
        <Button
          disabled={!hasWriteAccess}
          testId="add-new-sample"
          onClick={() =>
            openModal('SELECT_SAMPLES', {
              animalId: subjectId,
              handleCallback: fetchSamples,
            })
          }
        >
          Add new
        </Button>
      </div>
      {fetchError && <ApiErrorBanner error={fetchError} />}
      <Table
        columns={columns}
        data={samples as Array<Sample>}
        sortBy={[{ id: 'recorded', desc: false }]}
        className="tableOverflowVisible"
      />
    </div>
  );
};

export default Samples;
